import { useEffect, useState, useCallback } from 'react';
import { apiClient, ApiClientError } from '../lib/api';
import { formatRelative } from '../lib/time';
import type { PendingMessage } from '@shared/types';

const STORAGE_KEY = 'marthe.adminKey';

export function Admin() {
  useEffect(() => {
    document.body.dataset.route = '/admin';
    return () => {
      document.body.dataset.route = '';
    };
  }, []);

  const [adminKey, setAdminKey] = useState<string>(() => sessionStorage.getItem(STORAGE_KEY) ?? '');
  const [authenticated, setAuthenticated] = useState(() => Boolean(sessionStorage.getItem(STORAGE_KEY)));
  const [pending, setPending] = useState<PendingMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (key: string) => {
    setLoading(true);
    setError(null);
    try {
      const list = await apiClient.getPending(key);
      setPending(list);
      setAuthenticated(true);
      sessionStorage.setItem(STORAGE_KEY, key);
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 401) {
        setAuthenticated(false);
        sessionStorage.removeItem(STORAGE_KEY);
        setError('clé invalide');
      } else {
        setError(err instanceof ApiClientError ? err.message : 'erreur réseau');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authenticated && adminKey) void refresh(adminKey);
  }, [authenticated, adminKey, refresh]);

  async function onAuth(e: React.FormEvent) {
    e.preventDefault();
    if (!adminKey.trim()) return;
    await refresh(adminKey.trim());
  }

  async function onVerify(id: string) {
    try {
      await apiClient.verifyMessage(adminKey, id);
      setPending((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'erreur réseau');
    }
  }

  async function onReject(id: string) {
    try {
      await apiClient.rejectMessage(adminKey, id);
      setPending((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'erreur réseau');
    }
  }

  function logout() {
    sessionStorage.removeItem(STORAGE_KEY);
    setAdminKey('');
    setAuthenticated(false);
    setPending([]);
  }

  if (!authenticated) {
    return (
      <main className="m-grid-center" style={{ minHeight: '100vh', padding: 'var(--sp-6)' }}>
        <section style={{ maxWidth: 360, width: '100%' }}>
          <p className="m-label">marthe / admin</p>
          <h1 style={{ fontSize: 20, fontWeight: 400, margin: '8px 0 24px' }}>authentification</h1>
          <form className="m-form" onSubmit={onAuth}>
            <div className="m-field">
              <label className="m-label" htmlFor="key">x-admin-key</label>
              <input
                id="key"
                className="m-input"
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                autoFocus
                autoComplete="off"
              />
            </div>
            {error && (
              <p style={{ fontSize: 12, border: '1px solid var(--ink)', padding: 8 }}>{error}</p>
            )}
            <button className="m-btn" type="submit" disabled={!adminKey.trim() || loading}>
              {loading ? 'vérification…' : 'entrer'}
            </button>
          </form>
        </section>
      </main>
    );
  }

  return (
    <main style={{ minHeight: '100vh', padding: 'var(--sp-6)' }}>
      <section style={{ maxWidth: 720, margin: '0 auto' }}>
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: 'var(--sp-6)',
          }}
        >
          <div>
            <p className="m-label">marthe / admin</p>
            <h1 style={{ fontSize: 20, fontWeight: 400, margin: '4px 0 0' }}>
              modération · {pending.length} en attente
            </h1>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="m-btn m-btn--ghost" onClick={() => void refresh(adminKey)} disabled={loading}>
              {loading ? '…' : 'refresh'}
            </button>
            <button className="m-btn m-btn--ghost" onClick={logout}>logout</button>
          </div>
        </header>

        {error && (
          <p style={{ fontSize: 12, border: '1px solid var(--ink)', padding: 8, marginBottom: 16 }}>
            {error}
          </p>
        )}

        {pending.length === 0 && !loading ? (
          <p style={{ color: 'var(--fg-muted)', fontSize: 13 }}>
            aucun message en attente.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pending.map((msg) => (
              <li key={msg.id} className="m-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                  <strong style={{ fontWeight: 500 }}>{msg.title}</strong>
                  <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                    {formatRelative(msg.createdAt)}
                  </span>
                </div>
                <p style={{ whiteSpace: 'pre-wrap', margin: '8px 0', fontSize: 13 }}>{msg.body}</p>
                {msg.giftUrl && (
                  <p style={{ fontSize: 11, color: 'var(--fg-muted)', wordBreak: 'break-all' }}>
                    cagnotte → <a href={msg.giftUrl} target="_blank" rel="noreferrer">{msg.giftUrl}</a>
                  </p>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="m-btn" onClick={() => void onVerify(msg.id)}>approuver</button>
                  <button className="m-btn m-btn--ghost" onClick={() => void onReject(msg.id)}>rejeter</button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
